import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Bell, CalendarCheck, Funnel, CreditCard, ArrowDown } from "@phosphor-icons/react";
import { MaskedLine } from "@/components/Reveal";
import { StreamCanvas } from "@/components/StreamCanvas";

const CHIPS = [
    { icon: Bell, text: "New enquiry — kitchen extension · £4,800", pos: "right-[6%] top-[22%]", delay: 0 },
    { icon: CalendarCheck, text: "Viewing booked — Thursday 11:30", pos: "right-[16%] top-[46%]", delay: 1.4 },
    { icon: Funnel, text: "Lead qualified — score 86", pos: "right-[4%] top-[68%]", delay: 2.6 },
    { icon: CreditCard, text: "Payment received — £1,250", pos: "right-[20%] top-[82%]", delay: 3.4 },
];

export default function Hero({ entered }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
    const d = entered ? 0 : 4.2;

    return (
        <section ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
            <motion.div className="absolute inset-0" style={{ y: yImg }}>
                <img
                    src="/assets/img/hero.jpg"
                    alt="A business operating through a connected HIGHTS digital ecosystem"
                    className="h-[120%] w-full object-cover opacity-40"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-base" />
            </motion.div>
            <StreamCanvas className="opacity-60" />

            <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-center px-5 pt-24 md:px-10">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: d + 0.1, duration: 1 }}
                    className="font-mono text-[11px] uppercase tracking-[0.4em] text-white/50"
                    data-testid="hero-label"
                >
                    HIGHTS — Business Growth System
                </motion.p>
                <h1 className="mt-6 font-display font-black tracking-tighter" data-testid="hero-headline">
                    <MaskedLine delay={d + 0.25}>
                        <span className="block text-5xl leading-[0.95] text-white sm:text-6xl lg:text-[5rem]">YOUR BUSINESS.</span>
                    </MaskedLine>
                    <MaskedLine delay={d + 0.45}>
                        <span className="block text-5xl leading-[0.95] text-white sm:text-6xl lg:text-[5rem]">ENGINEERED</span>
                    </MaskedLine>
                    <MaskedLine delay={d + 0.65}>
                        <span className="block text-5xl leading-[0.95] text-white/60 sm:text-6xl lg:text-[5rem]">TO GROW.</span>
                    </MaskedLine>
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: d + 1.0, duration: 0.9 }}
                    className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/65 md:text-lg"
                >
                    Premium websites, AI employees, CRM, automation and optimisation —
                    connected into one system that works your business around the clock.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: d + 1.2, duration: 0.9 }}
                    className="mt-10 flex flex-col gap-3 sm:flex-row sm:gap-4"
                >
                    <Link
                        to="/book"
                        data-testid="hero-book-button"
                        className="bg-white px-9 py-4 text-center text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-white/80"
                    >
                        Book a free consultation
                    </Link>
                    <button
                        onClick={() => document.getElementById("problem")?.scrollIntoView({ behavior: "smooth" })}
                        data-testid="hero-story-button"
                        className="border border-white/25 px-9 py-4 text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-white/10"
                    >
                        See how it works
                    </button>
                </motion.div>
            </motion.div>

            <div className="pointer-events-none absolute inset-0 z-10 hidden lg:block" aria-hidden="true">
                {CHIPS.map((c) => (
                    <motion.div
                        key={c.text}
                        className={`absolute ${c.pos}`}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: [0, -10, 0] }}
                        transition={{
                            opacity: { delay: d + 1.6 + c.delay * 0.3, duration: 0.8 },
                            y: { delay: d + 1.6, duration: 7, repeat: Infinity, ease: "easeInOut", repeatDelay: c.delay },
                        }}
                    >
                        <div className="flex items-center gap-2.5 border border-white/12 bg-black/55 px-4 py-2.5 backdrop-blur-md">
                            <c.icon size={15} className="text-white/70" />
                            <span className="font-mono text-[10px] tracking-wider text-white/70">{c.text}</span>
                        </div>
                    </motion.div>
                ))}
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: d + 2.2 }}
                className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
                aria-hidden="true"
            >
                <ArrowDown size={18} className="animate-bounce text-white/40" />
            </motion.div>
        </section>
    );
}
